import React, { useEffect, useMemo, useState } from 'react';
import { App, Form, Input, Modal } from 'antd';
import {
  useCreateExpensePaymentMethodMutation,
  useGetExpensePaymentMethodsQuery,
} from '../../services/expensesApi';
import { ExpensePaymentMethodDto } from '../../types/expense';

interface Props {
  open: boolean;
  restaurantId: string;
  onClose: () => void;
  // CRITICAL contract: the caller (ExpenseFormDialog / ExpenseEditDialog) receives the
  // freshly-created DTO and is responsible for auto-selecting it in the parent dropdown
  // via form.setFieldsValue({ paymentMethodId: created.id }). Without this callback the
  // newly added method would be created in the catalog but the user would have to find
  // it again in the dropdown — defeating the inline-add UX the customer asked for.
  onCreated: (created: ExpensePaymentMethodDto) => void;
}

interface FormValues {
  name: string;
}

const QuickAddPaymentMethodModal: React.FC<Props> = ({ open, restaurantId, onClose, onCreated }) => {
  const [form] = Form.useForm<FormValues>();
  // AntD v5: static `import { message } from 'antd'` silently no-ops — must use App.useApp().
  const { message } = App.useApp();
  const [createMethod, { isLoading: isSaving }] = useCreateExpensePaymentMethodMutation();

  // Include inactive entries so the client-side dedup catches collisions against
  // soft-deleted names too — otherwise the backend 409 surfaces as a confusing
  // "duplicate" error after the user already typed the full name.
  const { data: existingMethods } = useGetExpensePaymentMethodsQuery(
    { restaurantId, includeInactive: true },
    { skip: !open }
  );

  const [serverError, setServerError] = useState<string | null>(null);

  useEffect(() => {
    if (open) {
      form.resetFields();
      setServerError(null);
    }
  }, [open, form]);

  const existingNamesLower = useMemo(
    () => new Set((existingMethods || []).map((m) => m.name.trim().toLowerCase())),
    [existingMethods]
  );

  const handleSubmit = async () => {
    setServerError(null);

    let values: FormValues;
    try {
      values = await form.validateFields();
    } catch {
      return;
    }

    const trimmed = values.name.trim();

    try {
      const created = await createMethod({
        restaurantId,
        data: { name: trimmed },
      }).unwrap();

      // Hand the DTO back BEFORE closing so the caller can setFieldsValue while the form
      // is still mounted. Order matters — closing first triggers destroyOnClose on the
      // parent which would unmount the dropdown before we set its value.
      onCreated(created);
      message.success('Método de pago agregado');
      onClose();
    } catch (err: any) {
      // 409 = race with another user creating the same name — show inline, not toast,
      // because the user is mid-flow in the form.
      const serverMessage = err?.data?.message;
      setServerError(serverMessage || 'No se pudo agregar el método de pago.');
    }
  };

  return (
    <Modal
      title="Agregar nuevo método de pago"
      open={open}
      onCancel={onClose}
      onOk={handleSubmit}
      okText="Agregar"
      cancelText="Cancelar"
      confirmLoading={isSaving}
      okButtonProps={{ disabled: isSaving }}
      destroyOnClose
      width={420}
    >
      <Form<FormValues> form={form} layout="vertical" autoComplete="off">
        <Form.Item
          name="name"
          label="Nombre"
          rules={[
            { required: true, message: 'El nombre es requerido' },
            { max: 100, message: 'Máximo 100 caracteres' },
            {
              validator: (_r, v) => {
                const trimmed = (v ?? '').trim();
                if (!trimmed) {
                  return Promise.resolve(); // covered by `required`
                }
                if (existingNamesLower.has(trimmed.toLowerCase())) {
                  return Promise.reject(
                    new Error('Ya existe un método con ese nombre (sin distinguir mayúsculas).')
                  );
                }
                return Promise.resolve();
              },
            },
          ]}
          // Re-validate after each keystroke so the duplicate message disappears as soon
          // as the user types something unique. validateTrigger defaults to onChange + onBlur.
          validateFirst
          extra={serverError ? <span style={{ color: 'var(--color-error-text)' }}>{serverError}</span> : null}
        >
          <Input
            maxLength={100}
            autoFocus
            placeholder="Ej. Tarjeta Bancomer, Transferencia Banamex…"
          />
        </Form.Item>
      </Form>
    </Modal>
  );
};

export default QuickAddPaymentMethodModal;
